<template>
  <v-card class="mx-auto" :loading="loading">
    <v-card-text>
      <entry-note
        :completion-date="entry.completion_date"
        :note="firstNote"
        :showLegs="false"
        :dealer="entry.responsive_dealer"
      ></entry-note>

      <v-expand-transition
        ><div v-if="expanded">
          <!-- Attachments from firstNote -->
          <entry-note
            :note="firstNote"
            :showHead="false"
            :dealer="entry.responsive_dealer"
          ></entry-note>

          <!-- All other notes -->

          <div v-for="note in otherNotes" :key="note.uuid" class="my-2">
            <v-divider class="mb-2" />
            <entry-note
              :note="note"
              :dealer="entry.responsive_dealer"
            ></entry-note>
          </div>
        </div>
      </v-expand-transition>

      <div class="text-overline"></div>
    </v-card-text>

    <v-form v-if="resolving">
      <v-divider class="mb-2" />
      <v-container>
        <v-card flat tile>
          <v-file-input
            v-model="resolveAttachments"
            :label="$t('components.logEntry.selectFiles')"
            clearable
            multiple
            show-size
            chips
            counter
            required
          ></v-file-input>
          <v-dialog
            ref="dateDialog"
            v-model="resolveDateDialog"
            :return-value.sync="resolveDate"
            persistent
            width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="resolveDate"
                :label="$t('components.logEntry.dateOfService')"
                prepend-icon="mdi-calendar"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="resolveDate" scrollable>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="modal = false">{{
                $t("components.logEntry.cancel")
              }}</v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dateDialog.save(resolveDate)"
                >{{ $t("components.logEntry.ok") }}</v-btn
              >
            </v-date-picker>
          </v-dialog>
          <v-textarea color="primary" v-model="resolveNotes">
            <template v-slot:label>
              <div>{{ $t("components.logEntry.notes") }} <small></small></div>
            </template>
          </v-textarea>
        </v-card>
      </v-container>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn text @click="resolving = !resolving">{{
          $t("components.logEntry.cancel")
        }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="saveAndResolve()">{{
          $t("components.logEntry.saveMarkAsResolved")
        }}</v-btn>
      </v-card-actions>
    </v-form>

    <div
      v-if="
        !resolving &&
          (expandable ||
            (entry.status && entry.status.toLowerCase() !== 'done'))
      "
    >
      <v-divider></v-divider>

      <v-card-actions>
        <v-dialog
          v-model="showingRequestAssistanceDialog"
          width="500"
          v-if="showRequestAssistance"
        >
          <template v-slot:activator="{ on }">
            <v-btn text color="primary" v-on="on">{{
              $t("components.logEntry.requestService")
            }}</v-btn>
          </template>

          <v-card>
            <v-card-title primary-title>
              {{ $t("components.logEntry.serviceAssistenceRequest") }}
            </v-card-title>

            <v-card-text>
              {{
                $t("components.logEntry.requestServiceFromServicingPartner", {
                  servicePartner: entry.responsive_dealer.name,
                })
              }}
              <br />
              <br />
              {{
                $t(
                  "components.logEntry.yourServicingPartnerWillBeNotifiedOfYourRequestContactYouToScheduleAnAppointment",
                )
              }}
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
              <v-btn text @click="showingRequestAssistanceDialog = false">
                {{ $t("components.logEntry.cancel") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="requestAssistance()">
                {{ $t("components.logEntry.yes") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-spacer v-if="showRequestAssistance"></v-spacer>

        <v-btn
          v-if="entry.status && entry.status.toLowerCase() !== 'done'"
          text
          @click="resolving = !resolving"
        >
          {{ $t("components.logEntry.resolve") }}</v-btn
        >

        <v-spacer v-if="expandable"></v-spacer>

        <v-btn icon v-if="expandable" @click="expanded = !expanded">
          <v-icon>{{
            expanded ? "mdi-chevron-up" : "mdi-chevron-down"
          }}</v-icon>
        </v-btn>
      </v-card-actions>
    </div>
  </v-card>
</template>

<script>
import EntryNote from "@/components/EntryNote.vue";
import { mapGetters, mapState } from "vuex";
import { updateLogEntry } from "@/api/log-entries";
import {
  entryAssistanceRequest,
  uploadNoteAttachment,
  uploadResolvingNoteAttachment,
  createResolvingLogNote,
  updateEntryStatus,
  loadEntry,
} from "@/api/log-entries";

export default {
  name: "log-entry",
  components: {
    EntryNote,
  },
  props: {
    entry: Object,
  },
  data: () => ({
    loading: false,
    expanded: false,
    resolving: false,
    resolveAttachments: [],
    resolveNotes: "",
    resolveDate: null,
    showingRequestAssistanceDialog: false,
    resolveDateDialog: false,
  }),
  created() {
    this.resolveDate = (this && this.entry && this.entry.completion_date
      ? new Date(this.entry.completion_date)
      : new Date()
    )
      .toISOString()
      .split("T")[0];
  },
  computed: {
    ...mapState({
      accessToken: (s) => s.accessToken,
      userBoatId: (s) => s.userBoatId,
    }),
    resolveCompletionDate: function() {
      return new Date(Date.parse(this.resolveDate));
    },
    showRequestAssistance: function() {
      return (
        this.entry.responsive_dealer &&
        this.entry.can_request_assistance &&
        !this.entry.is_assistance_requested &&
        this.entry.status &&
        this.entry.status.toLowerCase() !== "done"
      );
    },
    sortedNotes: function() {
      return this.entry.notes.concat().sort(function(a, b) {
        if (a.created > b.created) {
          return 1;
        }
        if (b.created > a.created) {
          return -1;
        }
        return 0;
      });
    },
    firstNote: function() {
      return this.sortedNotes[0];
    },
    otherNotes: function() {
      return this.sortedNotes.slice(1);
    },
    firstNoteAttachments: function() {
      if (this.firstNote && this.firstNote.attachments) {
        return this.firstNote.attachments;
      } else {
        return [];
      }
    },
    status: function() {
      if (this.entry.status.toLowerCase() === "due") {
        return "Upcoming";
      } else if (this.entry.status.toLowerCase() === "overdue") {
        return "Overdue";
      }
      return "Done";
    },
    statusColor: function() {
      if (this.entry.status.toLowerCase() === "due") {
        return "warning";
      } else if (this.entry.status.toLowerCase() === "overdue") {
        return "error";
      }
      return "default";
    },
    expandable: function() {
      return (
        this.otherNotes.length > 0 ||
        (this.firstNote &&
          this.firstNote.attachments &&
          this.firstNote.attachments.length > 0)
      );
    },
  },
  methods: {
    requestAssistance: function() {
      this.loading = true;
      this.showingRequestAssistanceDialog = false;

      entryAssistanceRequest(this.accessToken, this.userBoatId, this.entry.uuid)
        .then(() => {
          loadEntry(this.accessToken, this.userBoatId, this.entry.uuid).then(
            (newEntry) => {
              this.loading = false;
              this.expanded = true;

              this.$emit("update", this.entry.uuid, newEntry);
            },
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    saveAndResolve: function() {
      console.error(
        "This code is not functional. This code path no longer reachable from GUI. Left for future changes",
      );
      return;

      // this.loading = true;

      // let note = {
      //   content: this.resolveNotes,
      //   attachments: [],
      // };

      // // TODO: migrate to new direct file upload logic.

      // createResolvingLogNote(
      //   this.accessToken,
      //   this.userBoatId,
      //   this.entry.uuid,
      //   note
      // ).then((note) => {
      //   let logUUID = this.entry.uuid;
      //   let entryUUID = note.notes[note.notes.length - 1].uuid;

      //   return uploadEntryAttachments(
      //     this.accessToken,
      //     this.userBoatId,
      //     logUUID,
      //     entryUUID,
      //     this.resolveAttachments
      //   ).then(() => {
      //     let newStatus = {
      //       status: "DONE",
      //       completion_date: this.resolveCompletionDate,
      //     };
      //     // FIXME: ensure it stays expande.

      //     return updateEntryStatus(
      //       this.accessToken,
      //       this.userBoatId,
      //       this.entry.uuid,
      //       newStatus
      //     ).then((updatedEntry) => {
      //       this.loading = false;
      //       this.resolving = false;
      //       this.expanded = true;
      //       this.entry.status = updatedEntry.status;
      //       this.entry.completion_date = updatedEntry.completion_date;

      //       this.$emit("update", this.entry.uuid, updatedEntry);
      //     });
      //   });
      // });
    },
  },
};
</script>

<style scoped>
.attachment {
  width: 100px;
  height: 150px;
}
</style>
